import {
  useGetDutyRosterReleaseWorkflowsSuspense,
  useSetWorkAreaWeekStatus,
} from "@/orval/gen"
import { useInvalidate } from "@/typed-client/hooks/use-invalidate"
import { showErrorNotification } from "@/utils/form-submission-notifications"
import { getFormattedDate } from "@/utils/get-formatted-date"
import { CancelButton, SaveButton } from "@components/Buttons"
import { DefaultModal } from "@components/default-modal"
import { FormButtonGroup } from "@components/form-button-group"
import { Alert, ModalProps, Select, Stack, Text, Textarea } from "@mantine/core"
import { useForm } from "@mantine/form"
import dayjs from "dayjs"
import { useTranslation } from "react-i18next"

interface Props extends ModalProps {
  workAreaId: number
  date: string
  fromStatus: number | null
}

export function SetWorkAreaWeekStatusModal({
  workAreaId,
  date,
  fromStatus,
  ...modalProps
}: Props) {
  const { t } = useTranslation("app_server")
  const firstDayOfWeek = dayjs(date).startOf("week").format("YYYY-MM-DD")
  const lastDayOfWeek = dayjs(date).endOf("week").format("YYYY-MM-DD")
  const { data } = useGetDutyRosterReleaseWorkflowsSuspense({
    fromStatus: fromStatus ?? undefined,
  })
  const form = useForm({
    initialValues: {
      dutyRosterReleaseWorkflowId: data?.[0]?.dutyRosterReleaseWorkflowId,
      startDate: firstDayOfWeek,
      workAreaId,
      comment: "",
    },
    mode: "uncontrolled",
  })
  const invalidate = useInvalidate()
  const { mutateAsync: setStatus } = useSetWorkAreaWeekStatus({
    mutation: {
      onSuccess: () => {
        invalidate("/api/Orchestra/GetSchedule")
        modalProps.onClose()
      },
      onError: showErrorNotification,
    },
  })
  const handleSubmit = (values: typeof form.values) => {
    if (!!values.dutyRosterReleaseWorkflowId) {
      setStatus({
        data: values,
      })
    }
  }

  // initalize form on render, because the data is loaded after the modal is opened and we want to set the default value of the select to the first item of the data
  React.useEffect(() => {
    form.setFieldValue(
      "dutyRosterReleaseWorkflowId",
      data?.[0]?.dutyRosterReleaseWorkflowId
    )
    form.setFieldValue("startDate", firstDayOfWeek)
  }, [data, date])

  return (
    <DefaultModal {...modalProps} title={t("DutyScheduling_WeekStatus")}>
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <Text size="sm">
            {t("DutyScheduling_WeekStatus_Description", {
              firstDayOfWeek: getFormattedDate(firstDayOfWeek),
              lastDayOfWeek: getFormattedDate(lastDayOfWeek),
            })}
          </Text>
          {data.length === 0 ? (
            <Alert
              c={"gray"}
              title={t("DutyScheduling_WeekStatus_AlertTitle")}
              variant="light"
            >
              {t("DutyScheduling_WeekStatus_AlertDescription")}
            </Alert>
          ) : (
            <>
              <Select
                label={t("Status")}
                data={data.map((w) => ({
                  label: w.toStatusName,
                  value: w.dutyRosterReleaseWorkflowId.toString(),
                }))}
                value={form.getValues().dutyRosterReleaseWorkflowId?.toString()}
                key={form.key("dutyRosterReleaseWorkflowId")}
                {...form.getInputProps("dutyRosterReleaseWorkflowId")}
              />
              <Textarea
                label={t("Comment")}
                key={form.key("comment")}
                {...form.getInputProps("comment")}
              />
            </>
          )}
          <FormButtonGroup>
            <CancelButton onClick={modalProps.onClose} />
            <SaveButton
              disabled={!form.getValues().dutyRosterReleaseWorkflowId}
            />
          </FormButtonGroup>
        </Stack>
      </form>
    </DefaultModal>
  )
}
